import { type ComponentPropsWithoutRef, type ReactElement, type ReactNode, type SVGProps } from 'react';
import { BarChart, CartesianGrid, Rectangle, ResponsiveContainer } from 'recharts';
import {
  BAR_CHART_CATEGORY_GAP,
  BAR_CHART_MAX_BAR_SIZE,
  BAR_MARGIN_PER_MARKET,
  BAR_MARGIN_TIME_SERIES,
} from '@/domains/global-dashboard/utils/chart-utils';

/** Shared sizing for Recharts Bars across global-dashboard views. */
export const dashboardBarSizingProps = {
  maxBarSize: BAR_CHART_MAX_BAR_SIZE,
  barCategoryGap: BAR_CHART_CATEGORY_GAP,
} as const;

/** Wraps ResponsiveContainer — use when swapping BarChart / ComposedChart in the same viewport. */
export function DashboardChartViewport({
  children,
  className = 'h-[500px] w-full min-w-[320px]',
}: {
  children: ReactElement;
  className?: string;
}) {
  return (
    <div className={className}>
      <ResponsiveContainer width="100%" height="100%">
        {children}
      </ResponsiveContainer>
    </div>
  );
}

type BarChartOuterProps = ComponentPropsWithoutRef<typeof BarChart>;

export type DashboardBarChartProps = Omit<BarChartOuterProps, 'maxBarSize' | 'barCategoryGap'> & {
  /** Outer Tailwind sizing; defaults to per-market stacked column layout. */
  className?: string;
  /** Use time-series margins (month buckets) vs per-category market labels */
  variant?: 'perMarket' | 'timeSeries';
  children: ReactNode;
};

/** Standard global-dashboard vertical BarChart inside a ResponsiveContainer */
export function DashboardBarChart({
  data,
  margin,
  variant = 'perMarket',
  className = 'h-[500px] w-full min-w-[320px]',
  children,
  ...barChartProps
}: DashboardBarChartProps) {
  const resolvedMargin = margin ?? (variant === 'timeSeries' ? BAR_MARGIN_TIME_SERIES : BAR_MARGIN_PER_MARKET);
  return (
    <div className={className}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          {...dashboardBarSizingProps}
          data={data}
          margin={resolvedMargin}
          {...barChartProps}
        >
          {children}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export function DashboardBarGrid() {
  return <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />;
}

type PlotOffset = { top: number; left: number; width: number; height: number };

/**
 * Tooltip cursor: band over the full cartesian plot (uses Recharts `offset`, i.e. inner area after chart margins).
 * Pass as `<Tooltip cursor={<DashboardBarPlotHoverCursor />} />` on BarChart / ComposedChart with axis tooltips.
 */
export function DashboardBarPlotHoverCursor(
  props: SVGProps<SVGRectElement> & { offset?: PlotOffset },
) {
  const { offset } = props;
  if (!offset || offset.width <= 0 || offset.height <= 0) return null;
  return (
    <Rectangle
      x={offset.left + 0.5}
      y={offset.top + 0.5}
      width={Math.max(0, offset.width - 1)}
      height={Math.max(0, offset.height - 1)}
      fill="hsl(var(--muted) / 0.14)"
      stroke="none"
      pointerEvents="none"
    />
  );
}